import type { RootAction } from 'RootTypes';
import { matchPath } from 'react-router-dom';
import { getType } from 'typesafe-actions';
import type { TeamStats, TeamEventStats } from '@common/stats';
import { logOut } from '@state/modules/account/actions';
import { locationChange } from '@state/modules/router/actions';
import { ROUTES_WITH_TEAMS } from '@util/constants';

import * as Actions from './actions';
import type {
    Team,
    TeamID,
    Sport,
    TeamOrOrgListGroupedBySeason,
    AgeGroup,
    Season,
    CompetitionLevel,
    TeamType,
    TeamOpponent,
} from './types';
import { toTeamID, isListTeam } from './types';

export const emptyTeamStats = {
    batting: {
        playerStats: [],
        teamStats: [],
    },
    pitching: {
        playerStats: [],
        teamStats: [],
    },
    fielding: {
        playerStats: [],
        teamStats: [],
        playerCatchingStats: [],
        inningsPlayed: [],
    },
};

export const initialState: State = {
    list: [],
    meTeamsAndOrgs: undefined,
    seasonStats: emptyTeamStats,
    gameStats: {},
    playerStats: {
        batting: {
            playerStats: [],
            cumulativeStats: [],
        },
        pitching: {
            playerStats: [],
            cumulativeStats: [],
        },
        fielding: {
            playerStats: [],
            cumulativeStats: [],
            playerCatchingStats: [],
            cumulativeCatchingStats: [],
            inningsPlayed: [],
            cumulativeInningsPlayedStats: [],
        },
        offensiveSprayCharts: [],
        defensiveSprayCharts: [],
    },
    selectedTeam: {
        id: '' as TeamID,
        name: '',
        sport: '' as Sport,
        ageGroup: '' as AgeGroup,
        seasonName: '' as Season,
        seasonYear: 0,
        competitionLevel: '' as CompetitionLevel,
        teamType: '' as TeamType,
        seasonRecord: '',
        avatar_url: '',
    },
    opponents: undefined,
};

interface State {
    list: Team[];
    selectedTeam: Team;
    meTeamsAndOrgs?: TeamOrOrgListGroupedBySeason[];
    seasonStats: TeamStats;
    gameStats: { [k: TeamID]: TeamStats };
    playerStats: TeamEventStats;
    opponents?: TeamOpponent[];
    selectedOpponent?: TeamOpponent;
}

const getTeamIDFromURL = (URL: string) => {
    const pathIncludesTeamIDParam = (path: string): boolean => path.includes(':teamID');
    const pathsWithTeamIDs = [...Object.values(ROUTES_WITH_TEAMS).filter(pathIncludesTeamIDParam)];

    for (const path of pathsWithTeamIDs) {
        const match = matchPath<'teamID', string>({ end: false, path }, URL);

        if (match?.params?.teamID) {
            return toTeamID(match.params.teamID);
        }
    }

    return undefined;
};

const addTeamRecords = (
    teamRecords: { teamID: TeamID; record: string | undefined }[],
    meTeamsAndOrgs: TeamOrOrgListGroupedBySeason[] | undefined,
): TeamOrOrgListGroupedBySeason[] | undefined => {
    return meTeamsAndOrgs?.flatMap((seasonGroup) => {
        return Object.keys(seasonGroup).map((season) => {
            return {
                [season]: seasonGroup[season].map((teamOrOrg) => {
                    if (isListTeam(teamOrOrg)) {
                        const updateForTeam = teamRecords.find((x) => x.teamID === teamOrOrg.id);
                        if (updateForTeam) return { ...teamOrOrg, seasonRecord: updateForTeam.record };
                    }
                    return teamOrOrg;
                }),
            };
        });
    });
};

export const reducer = (state = initialState, action: RootAction): State => {
    switch (action.type) {
        case getType(locationChange): {
            const teamID = getTeamIDFromURL(action.payload.location.pathname);
            return {
                ...state,
                selectedTeam: { ...state.selectedTeam, id: teamID ? teamID : state.selectedTeam.id },
            };
        }

        case getType(logOut):
            return initialState;

        case getType(Actions.addTeams):
            return {
                ...state,
                list: action.payload,
            };

        case getType(Actions.addMeTeams):
            return {
                ...state,
                meTeamsAndOrgs: action.payload,
            };

        case getType(Actions.addMeTeamRecords):
            return {
                ...state,
                meTeamsAndOrgs: addTeamRecords(action.payload, state.meTeamsAndOrgs),
            };

        case getType(Actions.addSeasonStats):
            return {
                ...state,
                seasonStats: action.payload,
            };

        case getType(Actions.addGameStats):
            return {
                ...state,
                gameStats: { ...state.gameStats, [action.payload.teamID]: action.payload.gameStats },
            };

        case getType(Actions.clearSeasonStats):
            return {
                ...state,
                seasonStats: initialState.seasonStats,
            };

        case getType(Actions.clearAllGameStats):
            return {
                ...state,
                gameStats: initialState.gameStats,
            };

        case getType(Actions.clearGameStatsForTeam):
            return {
                ...state,
                gameStats: { ...state.gameStats, [action.payload.teamID]: undefined },
            };

        case getType(Actions.selectTeam):
            return {
                ...state,
                selectedTeam: action.payload,
            };

        case getType(Actions.addPlayerStats):
            return {
                ...state,
                playerStats: action.payload,
            };

        case getType(Actions.clearPlayerStats):
            return {
                ...state,
                playerStats: initialState.playerStats,
            };

        case getType(Actions.addTeamOpponents):
            return {
                ...state,
                opponents: action.payload,
            };

        case getType(Actions.selectTeamOpponent):
            return {
                ...state,
                selectedOpponent: action.payload,
            };

        default:
            return state;
    }
};
